import * as Yup from "yup";
import React, { useEffect, useMemo, useState } from "react";
import { FiMail } from "react-icons/fi";
import { Formik } from "formik";
import { MdClose, MdOutlineEdit } from "react-icons/md";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import ContainedButton from "components/layout/form/containedButton";
import TextField from "components/layout/form/textField";
import api from "services/api";

import {
  BtnClose,
  ButtonEdit,
  Container,
  Content,
  ContentField,
  ContentUpdateEmail,
  Field,
  Footer,
  FormContainer,
  Header,
  Label,
  Title,
} from "./styles";

type ModalNothingContainedProps = {
  show: boolean;
  onClose: () => void;
};

const ModalNothingContained = ({
  show,
  onClose,
}: ModalNothingContainedProps): JSX.Element => {
  const [status, setStatus] = useState("initial");
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [protocol, setProtocol] = useState(null);
  const selectedUnit = useSelector(
    // @ts-ignore
    (state) => state.selectUnitReducer.selectedUnit
  );

  const email = useMemo((): any => {
    return contacts?.find((contact) => contact.contactType === "EMAIL");
  }, [contacts]);

  const title = useMemo((): string => {
    switch (status) {
      case "initial":
      case "updateEmail":
      case "validateToken":
        return "Solicitar carta de nada consta";
      case "requestedFail":
      case "requestedSuccess":
        return "Status da sua solicitação:";
      default:
        return "";
    }
  }, [status]);

  const content = useMemo((): string => {
    switch (status) {
      case "initial":
      case "updateEmail":
      case "validateToken":
        return "Para o envio da <b>carta de nada consta</b> precisamos que confirme seu e-mail:";
      case "requestedFail":
        return "Não foi possível finalizar sua solicitação! Tente novamente mais tarde.";
      case "requestedSuccess":
        return "<b>Carta de nada consta</b> solicitada com sucesso! Em alguns instantes você a receberá em seu e-mail.";
      default:
        return "";
    }
  }, [status]);

  useEffect(() => {
    (async () => {
      await requestGetUserContacts();
    })();
  }, []);

  const requestGetUserContacts = async (): Promise<void> => {
    const headers = {
      access_token: localStorage.getItem("@UltragazEmpresarial:accessToken"),
    };
    const response = await api.get(
      `industrial/customer/v1/customers/me/contacts`,
      {
        headers,
      }
    );
    const { data } = response;
    setContacts(data?.filter((item) => item?.isValidated));
  };

  const requestNothingAppears = async (): Promise<void> => {
    setLoading(true);
    const headers = {
      access_token: localStorage.getItem("@UltragazEmpresarial:accessToken"),
    };

    const body = {
      recordType: "CartaDeNadaConsta",
      subject: "Pagamentos",
      reason: "Carta de Nada Consta",
      detail: "Solicitação carta de nada consta.",
      origin: "PORTAL_API_MI",
      sendMessage: true,
      customerSiteId: localStorage.getItem(
        "@UltragazEmpresarial:customerSiteId"
      ),
      contactIdSf: email?.contactIdSf,
      instanceId: selectedUnit?.instanceId,
    };

    try {
      const {
        data: { caseNumber },
      } = await api.post(`industrial/case/v1/cases`, body, {
        headers,
      });

      setProtocol(caseNumber);
      setStatus("requestedSuccess");
    } catch {
      setStatus("requestedFail");
    }
    setLoading(false);
  };

  const handleOnClose = (): void => {
    setStatus("initial");
    onClose();
  };

  const validationToken = async (token): Promise<boolean> => {
    const userId = localStorage.getItem("@UltragazEmpresarial:userId");
    const headers = {
      access_token: localStorage.getItem("@UltragazEmpresarial:accessToken"),
    };

    const bodyToken = {
      token,
    };

    let success;

    try {
      await api.post(
        `industrial/customer/v1/users/${userId}/validations`,
        bodyToken,
        {
          headers,
        }
      );

      success = true;
      await requestGetUserContacts();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ?? "Ocorreu um erro interno na aplicação"
      );
      success = false;
    }

    return success;
  };

  const sendToken = async (value): Promise<boolean> => {
    const userId = localStorage.getItem("@UltragazEmpresarial:userId");
    const siteId = localStorage.getItem("@UltragazEmpresarial:customerSiteId");
    const headers = {
      access_token: localStorage.getItem("@UltragazEmpresarial:accessToken"),
    };
    const bodyContact = {
      contact: value,
      contactType: "EMAIL",
      siteId,
    };

    try {
      const createContact = await api.post(
        `industrial/customer/v1/users/${userId}/contacts`,
        bodyContact,
        {
          headers,
        }
      );

      const contactId = createContact.headers.location.split("/").pop();

      const bodyToken = {
        contactId,
        tokenType: "REGISTER",
      };

      const responseToken = await api.post(
        `industrial/customer/v1/users/${userId}/tokens`,
        bodyToken,
        {
          headers,
        }
      );

      toast.warn(`Enviamos um código de verificação para ${value}`);

      return !!responseToken?.status;
    } catch (error) {
      if (error?.response?.data?.code === "422.010") {
        toast.error("Este e-mail já está cadastro para este apartamento/loja.");
      } else {
        toast.error(
          error?.response?.data?.message ||
            "Ocorreu um erro interno na aplicação"
        );
      }

      return false;
    }
  };

  return (
    <Modal show={show} onHide={handleOnClose} animation={false} centered>
      <Container>
        <Header>
          <Title>{title}</Title>

          <BtnClose onClick={handleOnClose}>
            <MdClose />
          </BtnClose>
        </Header>
        <Content>
          <p>
            <strong>
              Importante: A carta de quitação de débitos &quot;nada consta&quot;
              será enviada somente para os clientes que não possuem boletos
              pendentes de pagamento.
            </strong>
          </p>
          <p dangerouslySetInnerHTML={{ __html: content }} />
          {status === "requestedSuccess" && <p>Protocolo aberto: {protocol}</p>}
          {status === "initial" && (
            <ContentUpdateEmail>
              <Label>
                <span>E-mail cadastrado:</span>
                <span>{email?.contact}</span>
              </Label>
              <ButtonEdit onClick={() => setStatus("updateEmail")}>
                <MdOutlineEdit />
                <span>Editar e-mail</span>
              </ButtonEdit>
            </ContentUpdateEmail>
          )}
          {status === "updateEmail" && (
            <Formik
              initialValues={{
                email: "",
              }}
              onSubmit={async ({ email }) => {
                setLoading(true);

                const result = await sendToken(email);

                if (result) {
                  setStatus("validateToken");
                }

                setLoading(false);
              }}
              validationSchema={Yup.object().shape({
                email: Yup.string()
                  .required("É necessário digitar um e-mail")
                  .email("E-mail inválido"),
              })}
              render={({
                handleChange,
                handleBlur,
                values,
                errors,
                touched,
              }) => (
                <FormContainer>
                  <ContentField>
                    <Field>
                      <TextField
                        LeadingIcon={FiMail}
                        type="text"
                        name="email"
                        label="Email *"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.email}
                        errorCondition={errors.email && touched.email}
                        errorMessage={errors.email}
                        disabled={loading}
                      />
                    </Field>
                    <ContainedButton
                      type="submit"
                      disabled={!values.email || loading}
                      loading={loading}
                    >
                      Validar e-mail
                    </ContainedButton>
                  </ContentField>
                </FormContainer>
              )}
            />
          )}
          {status === "validateToken" && (
            <Formik
              initialValues={{
                token: "",
              }}
              onSubmit={async ({ token }) => {
                setLoading(true);

                const result = await validationToken(token);

                if (result) {
                  setStatus("initial");
                }

                setLoading(false);
              }}
              validationSchema={Yup.object().shape({
                token: Yup.string()
                  .required("É necessário digitar um token")
                  .length(6, "Token inválido"),
              })}
              render={({
                handleChange,
                handleBlur,
                values,
                errors,
                touched,
              }) => (
                <FormContainer>
                  <ContentField>
                    <Field>
                      <TextField
                        LeadingIcon={FiMail}
                        type="text"
                        name="token"
                        label="Token *"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.token}
                        errorCondition={errors.token && touched.token}
                        errorMessage={errors.token}
                        disabled={loading}
                      />
                    </Field>
                    <ContainedButton
                      type="submit"
                      disabled={
                        !values.token || values.token?.length !== 6 || loading
                      }
                      loading={loading}
                    >
                      Validar token
                    </ContainedButton>
                  </ContentField>
                </FormContainer>
              )}
            />
          )}
        </Content>
        <Footer>
          {["initial", "updateEmail", "validateToken"]?.includes(status) ? (
            <ContainedButton
              onClick={requestNothingAppears}
              disabled={
                ["updateEmail", "validateToken"]?.includes(status) || loading
              }
              loading={loading}
              block
            >
              Solicitar
            </ContainedButton>
          ) : (
            <ContainedButton onClick={handleOnClose} block>
              Ok
            </ContainedButton>
          )}
        </Footer>
      </Container>
    </Modal>
  );
};

export default ModalNothingContained;
